import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { getUser, localStorageHelper } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { WeavePopNotification } from '@frontend/types';
import { theme } from '@frontend/theme';
import { ChecklistField, Modal, NakedButton, Text, useFormField } from '@frontend/design-system';
import { useNotificationPersonalizationMap } from '../notification-personalization/notification-personalization';

const MotionNakedButton = motion(NakedButton);

interface MutedPopsProps {
  notificationType: WeavePopNotification['type'];
  onComplete: () => void;
  onInteraction?: () => void;
  duration?: number;
}

export const MutedPops = ({ notificationType, onComplete, onInteraction, duration = 5 }: MutedPopsProps) => {
  const notificationPersonalizationMap = useNotificationPersonalizationMap();
  const { color } = notificationPersonalizationMap(notificationType);
  const [isActive, setIsActive] = useState(true);
  const timeoutRef = useRef<number>();

  const startTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(onComplete, duration * 1000);
    setIsActive(true);
  }, [onComplete, duration]);

  const pauseTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsActive(false);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearTimeout(timeoutRef.current);
  }, [startTimer]);

  const handleClick = () => {
    if (onInteraction) {
      onInteraction();
    }
  };

  return (
    <MotionNakedButton
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
      onFocus={pauseTimer}
      onBlur={startTimer}
      tabIndex={0}
      onClick={handleClick}
      css={css`
        display: flex;
        gap: ${theme.spacing(1)};
        padding: ${theme.spacing(1)};
        background-color: ${color.background};
        border-radius: ${theme.borderRadius.medium};
        color: ${color.primary};
        position: relative;
        overflow: hidden;
        border: 1px solid ${color.primary};
        align-items: center;
        transition: background-color 0.3s ease-in-out;
        :hover {
          background-color: ${color.hover};
        }
      `}
    >
      <Icon name='mute-small' />
      <Text size='small' weight='bold'>
        Muted pop-ups
      </Text>
      <Icon name='pause-small' />
      {isActive && (
        <motion.div
          initial={{ width: '100%' }}
          animate={{ width: 0 }}
          transition={{ duration, ease: 'linear' }}
          css={css`
            position: absolute;
            bottom: 0;
            left: 0;
            height: 2px;
            background-color: ${color.primary};
          `}
        />
      )}
    </MotionNakedButton>
  );
};

type MutePopsModalDialogProps = {
  show: boolean;
  onClose: () => void;
  onMute: (ignoreFutureModals: boolean) => void;
};

export const handleIgnoreMuteModal = (userId?: string) => {
  const ignoreUpdateLocationGroupModal = localStorageHelper.get<boolean>(`ignoreMuteNotificationsModal-${userId}`);
  return ignoreUpdateLocationGroupModal;
};

const handleSetIgnoreMuteModal = (userId?: string) => {
  localStorageHelper.create(`ignoreMuteNotificationsModal-${userId}`, 'true');
};

export const MutePopsModalDialog = ({ show, onClose, onMute }: MutePopsModalDialogProps) => {
  const ignoreModalChecklist = useFormField({
    type: 'checklist',
  });

  const handleConfirmMute = () => {
    const ignoreFuture = !!ignoreModalChecklist.value.length;
    onMute(ignoreFuture);
  };

  if (!show) {
    return null;
  }

  return (
    <Modal
      minWidth={605}
      css={css`
        padding: ${theme.spacing(2, 0)};
      `}
      show={show}
      onClose={onClose}
    >
      <Modal.Header onClose={onClose}>Mute All Pop-Up Notifications</Modal.Header>
      <Modal.Body>
        <Text>
          Muting will pause all pop up notifications. All notifications will still be available in your Notification
          Center.
        </Text>
        <ChecklistField {...ignoreModalChecklist} label='' name='ignore-update-location-group-modal'>
          <ChecklistField.Option
            css={css`
              padding: 0;
              margin: 0;
            `}
            name='ignore-update-location-group-modal-box'
          >
            <Text color='light'>{`Don’t show this message again`}</Text>
          </ChecklistField.Option>
        </ChecklistField>
      </Modal.Body>
      <Modal.Footer
        css={css`
          border-top: 1px solid ${theme.colors.neutral20};
          padding-top: ${theme.spacing(2)};
        `}
        primary={{
          label: 'Mute',
          onClick: handleConfirmMute,
        }}
        secondary={{
          label: 'Cancel',
          onClick: onClose,
        }}
      />
    </Modal>
  );
};

interface MuteFlowManagerProps {
  notificationType: WeavePopNotification['type'];
  onMuteConfirmed: () => Promise<void>;
  onFlowDismissed: () => void;
  onFlowCompleted?: () => void;
  onManageMuteSettings?: () => void;
}

export const MuteFlowManager = ({
  notificationType,
  onMuteConfirmed,
  onFlowDismissed,
  onManageMuteSettings,
  onFlowCompleted,
}: MuteFlowManagerProps) => {
  const user = getUser();
  const [currentStep, setCurrentStep] = useState<'idle' | 'modal' | 'confirmation_bar'>('idle');

  useEffect(() => {
    const shouldSkipModal = handleIgnoreMuteModal(user?.userID);
    if (shouldSkipModal) {
      onMuteConfirmed().then(() => {
        setCurrentStep('confirmation_bar');
      });
    } else {
      setCurrentStep('modal');
    }
  }, [user?.userID, onMuteConfirmed]);

  const handleModalMute = async (ignoreFutureModals: boolean) => {
    if (ignoreFutureModals) {
      handleSetIgnoreMuteModal(user?.userID);
    }
    await onMuteConfirmed();
    setCurrentStep('confirmation_bar');
  };

  const handleModalClose = () => {
    setCurrentStep('idle');
    onFlowDismissed();
  };

  const handleConfirmationBarComplete = () => {
    setCurrentStep('idle');
    if (onFlowCompleted) {
      onFlowCompleted();
    }
  };

  const handleConfirmationBarInteraction = () => {
    if (onManageMuteSettings) {
      onManageMuteSettings();
    } else {
      onFlowDismissed();
    }
  };

  if (currentStep === 'modal') {
    return <MutePopsModalDialog show={true} onClose={handleModalClose} onMute={handleModalMute} />;
  }

  if (currentStep === 'confirmation_bar') {
    return (
      <MutedPops
        notificationType={notificationType}
        onComplete={handleConfirmationBarComplete}
        onInteraction={handleConfirmationBarInteraction}
      />
    );
  }

  return null;
};
